import { useMemo, useState } from 'react';
import { ALL_VENDORS } from '../config';
import { MultiSelect } from '@mantine/core';

export const RestaurantsSelect: React.FC<{
  onSelectVendors: (selectedVendors: string[]) => void;
  vendors: { name: string; id: string }[];
}> = ({ onSelectVendors, vendors }) => {
  // Transform vendors to match MultiSelect expected format
  const vendorOptions = useMemo(
    () =>
      vendors.map((v: { name: string; id: string }) => ({
        value: v.id,
        label: v.name,
      })),
    [vendors]
  );

  // default: all selected
  const [value, setValue] = useState<string[]>(() =>
    vendors.map((v: { name: string; id: string }) => v.id)
  );

  const isAllSelected = value.length === vendorOptions.length && vendorOptions.length > 1;

  // add synthetic "All" item to dropdown
  const data = useMemo(
    () =>
      vendorOptions.length > 1
        ? [{ value: ALL_VENDORS, label: 'All' }, ...vendorOptions]
        : vendorOptions,
    [vendorOptions]
  );

  const handleChange = (next: string[]) => {
    if (next.length === 0) return;
    const hasAll = next.includes(ALL_VENDORS);

    // clicked "All"
    if (hasAll && !isAllSelected) {
      const allVendorIds = vendorOptions.map((v: { value: string; label: string }) => v.value);
      setValue(allVendorIds);
      onSelectVendors(allVendorIds);
      return;
    }

    // if "All" is selected and user unselects it
    if (!hasAll && isAllSelected) {
      setValue([]); // or keep previous, depends on your UX
      onSelectVendors([]);
      return;
    }

    // normal selection (remove ALL if present)
    const cleaned = next.filter((v) => v !== ALL_VENDORS);
    setValue(cleaned);
    onSelectVendors(cleaned);
  };

  // what we show in input:
  // if all selected -> show only "All" pill
  const displayValue = isAllSelected ? [ALL_VENDORS] : value;

  return (
    <MultiSelect
      w={400}
      data={data}
      value={displayValue}
      onChange={handleChange}
      searchable={false}
      styles={{
        pillsList: {
          flexWrap: 'nowrap',
          overflow: 'hidden',
          minWidth: 0,
          maxWidth: '100%',
        },
      }}
    />
  );
};
